import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Package, Truck, CheckCircle2, Clock, ArrowRight, MapPin,
  CreditCard, FileText, Phone, MessageSquare, ShieldCheck, ChevronRight
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { publicOrderTrack } from "@/lib/sellerData";
import { formatRupiah, formatDate } from "@/lib/dummyData";

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [phone, setPhone] = useState("");
  const [tracked, setTracked] = useState(null);

  const handleTrack = (e) => {
    e?.preventDefault();
    if (orderId.length < 4) {
      toast.error("Masukkan nomor pesanan yang valid");
      return;
    }
    setTracked(publicOrderTrack);
    toast.success("Pesanan ditemukan!");
  };

  const useDemo = () => {
    setOrderId(publicOrderTrack.id);
    setPhone("0812 0000 0000");
    setTimeout(() => setTracked(publicOrderTrack), 300);
  };

  return (
    <div className="mx-auto max-w-5xl px-5 lg:px-10 py-10 lg:py-16">
      <div className="text-center mb-10 animate-fade-up">
        <div className="pill-tag bg-amber-warm/15 text-amber-700 mb-4 mx-auto">Lacak Pesanan</div>
        <h1 className="font-display text-4xl lg:text-6xl font-bold text-ink tracking-tight">
          Di mana <span className="font-editorial italic text-forest">pesanan</span> saya?
        </h1>
        <p className="text-ink/55 mt-3 max-w-xl mx-auto">
          Belanja sebagai tamu tanpa daftar? Lacak status pesanan Anda di sini —
          cukup masukkan nomor pesanan dan nomor HP.
        </p>
      </div>

      {/* Search form */}
      <Card className="rounded-3xl border-stone-soft p-6 lg:p-8 animate-fade-up" style={{ animationDelay: "120ms" }}>
        <form onSubmit={handleTrack} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">No. Pesanan</Label>
            <Input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="ORD-202602-0142"
              className="bg-cream border-stone-soft rounded-lg h-11 font-mono"
              data-testid="track-order-id"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">No. HP Pemesan</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+62 812..."
              className="bg-cream border-stone-soft rounded-lg h-11"
              data-testid="track-order-phone"
            />
          </div>
          <Button type="submit" className="rounded-full bg-forest hover:bg-forest-dark text-white h-11 px-6" data-testid="track-order-submit">
            <Search size={15} /> Lacak
          </Button>
        </form>

        <div className="flex items-center gap-3 mt-4 text-xs">
          <button type="button" onClick={useDemo} className="text-forest font-semibold hover:underline" data-testid="track-order-demo">
            Coba dengan pesanan demo →
          </button>
          <span className="text-ink/55">·</span>
          <span className="text-ink/55">Nomor pesanan dikirim ke email/SMS saat checkout.</span>
        </div>
      </Card>

      {/* Result */}
      {tracked && (
        <div className="mt-10 space-y-6 animate-fade-up">
          {/* Status hero */}
          <Card className="rounded-3xl border-stone-soft p-7 bg-forest text-cream relative overflow-hidden grain-overlay blob-bg">
            <div className="relative z-10 grid lg:grid-cols-3 gap-6 items-center">
              <div className="lg:col-span-2">
                <div className="text-xs uppercase tracking-[0.22em] text-amber-warm font-semibold">{tracked.id}</div>
                <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2">
                  Pesanan Anda <span className="font-editorial italic text-amber-warm">{tracked.currentStatus.toLowerCase()}</span>
                </h2>
                <p className="text-cream/85 mt-2">Estimasi tiba <span className="font-semibold text-amber-warm">{formatDate(tracked.eta)}</span> · No. Resi <span className="font-mono">{tracked.tracking}</span></p>
              </div>
              <div className="rounded-2xl bg-white/10 backdrop-blur p-5 border border-white/15">
                <Truck size={32} className="text-amber-warm animate-float" />
                <div className="text-[10px] uppercase tracking-wider text-amber-warm mt-3 font-semibold">Kurir</div>
                <div className="font-display font-bold text-lg">{tracked.shipping}</div>
                <div className="text-xs text-cream/70 mt-1">Pembayaran: {tracked.payment}</div>
              </div>
            </div>
          </Card>

          {/* Timeline + Details */}
          <div className="grid lg:grid-cols-5 gap-6">
            <Card className="lg:col-span-3 rounded-3xl border-stone-soft p-7">
              <h3 className="font-display font-semibold text-lg text-ink mb-6">Perjalanan Pesanan</h3>
              <div className="relative space-y-5">
                {tracked.timeline.map((t, i) => (
                  <div key={i} className="flex gap-4 items-start animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full grid place-items-center ring-4 shrink-0 transition-all ${
                        t.active ? "bg-amber-warm text-ink ring-amber-warm/30 animate-pulse-soft" :
                        t.done ? "bg-forest text-white ring-forest/15" :
                        "bg-stone-soft text-ink/55 ring-stone-soft/30"
                      }`}>
                        {t.done ? <CheckCircle2 size={17} /> : <Clock size={17} />}
                      </div>
                      {i < tracked.timeline.length - 1 && <div className={`w-0.5 h-12 mt-1 ${t.done ? "bg-forest" : "bg-stone-soft"}`}></div>}
                    </div>
                    <div className="flex-1 pt-1.5">
                      <div className={`font-display font-semibold ${t.active ? "text-amber-700" : t.done ? "text-ink" : "text-ink/55"}`}>
                        {t.status}
                        {t.active && <Badge className="ml-2 bg-amber-warm text-ink hover:bg-amber-warm rounded-full text-[10px]">SEKARANG</Badge>}
                      </div>
                      <div className="text-xs text-ink/55 mt-0.5">{t.time}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mt-7 pt-5 border-t border-stone-soft">
                <Button variant="outline" className="rounded-full border-stone-soft" data-testid="track-action-contact"><MessageSquare size={14} /> Hubungi Kurir</Button>
                <Button variant="outline" className="rounded-full border-stone-soft" data-testid="track-action-invoice"><FileText size={14} /> Unduh Invoice</Button>
                <Button variant="outline" className="rounded-full border-stone-soft" data-testid="track-action-cs"><Phone size={14} /> Customer Service</Button>
              </div>
            </Card>

            <Card className="lg:col-span-2 rounded-3xl border-stone-soft p-7">
              <h3 className="font-display font-semibold text-lg text-ink mb-4">Rincian Pesanan</h3>
              <div className="space-y-3">
                {tracked.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-3 pb-3 border-b border-stone-soft/60 last:border-0 last:pb-0" data-testid={`track-item-${i}`}>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-ink">{it.name}</div>
                      <div className="text-xs text-ink/55">{it.qty} × {formatRupiah(it.price)}</div>
                    </div>
                    <div className="font-display font-semibold text-sm text-ink">{formatRupiah(it.qty * it.price)}</div>
                  </div>
                ))}
              </div>
              <Separator className="my-4 bg-stone-soft" />
              <div className="flex justify-between items-baseline">
                <span className="text-ink/55 text-sm">Total Pembayaran</span>
                <span className="font-display font-bold text-2xl text-forest">{formatRupiah(tracked.total)}</span>
              </div>

              <div className="mt-5 rounded-xl bg-cream p-4 flex gap-3">
                <MapPin size={18} className="text-forest shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink/55 font-semibold">Pembeli</div>
                  <div className="text-sm font-medium text-ink mt-1">{tracked.buyerName}</div>
                  <div className="text-xs text-ink/55 mt-1">Pesan tgl {formatDate(tracked.date)}</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Upgrade CTA */}
          <Card className="rounded-3xl bg-amber-warm text-ink p-7 relative overflow-hidden grain-overlay">
            <div className="relative z-10 grid lg:grid-cols-3 gap-5 items-center">
              <div className="lg:col-span-2">
                <div className="text-xs uppercase tracking-[0.22em] text-ink/70 font-semibold">Tahukah Anda?</div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold mt-2">
                  Jadi anggota → <span className="font-editorial italic">hemat hingga 15%</span> + dapat SHU
                </h3>
                <p className="text-ink/75 mt-2 text-sm">
                  Dengan menjadi anggota, harga semua produk yang Anda beli hari ini bisa lebih hemat —
                  dan setiap belanja menghitung sebagai jasa usaha untuk SHU tahunan Anda.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:items-end">
                <Link to="/daftar"><Button className="rounded-full bg-ink text-cream hover:bg-ink/90 w-full lg:w-auto" data-testid="track-cta-join">Daftar Anggota <ArrowRight size={15} /></Button></Link>
                <Link to="/profil"><Button variant="outline" className="rounded-full border-ink/30 text-ink hover:bg-ink/10 w-full lg:w-auto">Pelajari Manfaat</Button></Link>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Help section if not tracked yet */}
      {!tracked && (
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "Aman", d: "Pencarian aman, hanya pemilik pesanan dengan no HP yang valid yang dapat melacak." },
            { icon: Truck, t: "Real-time", d: "Status pesanan diperbarui mengikuti pergerakan kurir, otomatis." },
            { icon: ChevronRight, t: "Lanjutkan ke anggota", d: "Senang dengan layanan? Daftar anggota dan dapatkan harga eksklusif + SHU." },
          ].map((h, i) => (
            <Card key={i} className="rounded-2xl border-stone-soft p-6 hover-lift animate-fade-up" style={{ animationDelay: `${i * 100 + 100}ms` }}>
              <div className="w-11 h-11 rounded-xl bg-forest/10 text-forest grid place-items-center"><h.icon size={20} /></div>
              <h4 className="font-display font-semibold text-ink mt-4">{h.t}</h4>
              <p className="text-sm text-ink/55 mt-2 leading-relaxed">{h.d}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
